import { memo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { FieldArray, FieldInputProps, FieldMetaProps, getIn } from "formik";
import { Box, IconButton, Typography } from "@mui/material";
import AppButton from "../../components/AppButton";
import { GridRowModel, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import {
  FieldGroupColumnFormDetails,
  FieldGroupColumnFormDetailsFilled,
} from "../interfaces";
import { FormBuilder } from "../FormBuilder";
import { GenericObject } from "../../services/Price";
import { createInitialValues } from "../EditForm";
import { TooltipLabel } from "../../components/Tooltips";

const SX_INLINE_BUTTON_WIDTH = "38px";

export function mergeArr(
  fieldsOptions: FieldGroupColumnFormDetailsFilled | FieldGroupColumnFormDetails
) {
  const fields = fieldsOptions.columns.map((col) => col.fields);

  return fields.reduce((acc, curr) => {
    return acc.concat(curr);
  }, []);
}

const FieldGroupForm = memo(
  ({
    dynamicPath,
    fieldsOptions,
    keyFieldPath,
    multipleSelection,
    isNestedForm,
    nbFieldGroup,
    onRemoveGroup,
    row,
    rows,
  }: {
    dynamicPath?: boolean;
    fieldsOptions: FieldGroupColumnFormDetailsFilled;
    keyFieldPath: string;
    multipleSelection?: GridRowSelectionModel;
    isNestedForm: boolean;
    nbFieldGroup: number;
    onRemoveGroup: () => void;
    row?: GridRowModel;
    rows?: GridRowModel[];
  }) => {
    const intl = useIntl();

    return (
      <Box
        sx={{
          py: isNestedForm ? 0 : 1,
          m: 0,
        }}
      >
        {fieldsOptions.removeButtonDisplay === "button" && (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "end",
            }}
          >
            <AppButton
              intent="tonal"
              aria-label="delete"
              disabled={
                !!fieldsOptions.limitMin &&
                nbFieldGroup <= fieldsOptions.limitMin
              }
              startIcon={<DeleteIcon color="error" />}
              onClick={() => onRemoveGroup()}
              sx={{ mx: 1 }}
            >
              {intl.formatMessage({ id: "DELETE" })}
            </AppButton>
          </Box>
        )}
        <Box sx={{ display: "flex" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            {fieldsOptions.columns.map((col, index) => (
              <Box
                sx={{ width: col.flex }}
                key={`${col.columnLabel}-form-${index}`}
              >
                <FormBuilder
                  keyFieldPath={keyFieldPath}
                  fieldsOptions={col.fields}
                  row={row}
                  rows={rows}
                  multipleSelection={multipleSelection}
                  dynamicPath={dynamicPath}
                  isNestedForm={true}
                />
              </Box>
            ))}
          </Box>
          {fieldsOptions.removeButtonDisplay === "inline" && (
            <IconButton
              aria-label="close"
              disabled={
                !!fieldsOptions.limitMin &&
                nbFieldGroup <= fieldsOptions.limitMin
              }
              sx={{
                alignSelf: "start",
                color: "text.secondary",
                padding: 0,
                backgroundColor: "background.paper",
                borderRadius: "6px",
                height: "38px",
                width: SX_INLINE_BUTTON_WIDTH,
                mt: 1,
                mx: 1,
                mb: 0.5,
                "&.Mui-disabled": {
                  backgroundColor: "action.disabledBackground",
                },
              }}
              title={intl.formatMessage({ id: "DELETE" })}
              onClick={() => onRemoveGroup()}
            >
              <DeleteIcon sx={{ fontSize: "1.25rem" }} />
            </IconButton>
          )}
        </Box>
      </Box>
    );
  }
);

function redistributePercent(
  form: any,
  fieldName: string,
  newLength: number,
  keys: string[]
) {
  const base = Math.floor(10000 / newLength);
  const remainder = 10000 - base * (newLength - 1);
  for (let i = 0; i < newLength; i++) {
    keys.forEach((key) => {
      const val = ((i < newLength - 1 ? base : remainder) / 100).toFixed(2);
      form.setFieldValue(`${fieldName}[${i}].${key}`, val);
    });
  }
}

const FieldGroupContent = ({
  fieldName,
  fieldsOptions,
  mergedFields,
  meta,
  row,
  rows,
  multipleSelection,
  dynamicPath,
  isNested,
  unshift,
  remove,
  form,
}: {
  fieldName: string;
  fieldsOptions: FieldGroupColumnFormDetailsFilled;
  mergedFields: any[];
  meta: FieldMetaProps<any>;
  row?: GridRowModel;
  rows?: GridRowModel[];
  multipleSelection?: GridRowSelectionModel;
  dynamicPath?: boolean;
  isNested: boolean;
  unshift: (value: any) => void;
  remove: (index: number) => void;
  form: any;
}) => {
  const intl = useIntl();
  const fieldGroupArray: GenericObject[] = getIn(form.values, fieldName) ?? [];

  return (
    <Box
      sx={{
        backgroundColor: "background.sunken",
        borderRadius: "6px",
        padding: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Typography component={"p"}>
          <FormattedMessage id={fieldsOptions.label} />
        </Typography>
        <AppButton
          disabled={
            fieldsOptions.limit
              ? fieldGroupArray.length >= fieldsOptions.limit
              : false
          }
          sx={{
            color: "text.primary",
            py: 0,
            px: fieldsOptions.buttonLabel ? 1.25 : 0,
            textTransform: "none",
            minWidth: "32px",
          }}
          onClick={() => {
            unshift(createInitialValues(mergedFields, null));
            if (fieldsOptions.autoFillPercent) {
              redistributePercent(
                form,
                fieldName,
                fieldGroupArray.length + 1,
                fieldsOptions.autoFillPercent
              );
            }
          }}
        >
          {fieldsOptions.buttonLabel ? (
            <FormattedMessage id={fieldsOptions.buttonLabel} />
          ) : (
            <AddIcon fontSize="small" />
          )}
        </AppButton>
      </Box>
      {fieldGroupArray.length > 0 && (
        <Box
          sx={{
            display: "flex",
            my: 1,
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            {fieldsOptions.columns.map((col, index) => (
              <Box
                key={`header-${col.columnLabel}-${index}`}
                sx={{ width: col.flex }}
              >
                {col.columnTooltip ? (
                  <TooltipLabel
                    label={<FormattedMessage id={col.columnLabel} />}
                    tooltipLabel={col.columnTooltip}
                    sxStyles={{ justifyContent: "center" }}
                  />
                ) : (
                  <Typography sx={{ textAlign: "center" }}>
                    <FormattedMessage id={col.columnLabel} />
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
          {fieldsOptions.removeButtonDisplay === "inline" && (
            <Box
              sx={(theme) => ({
                width: `calc(${SX_INLINE_BUTTON_WIDTH} + ${theme.spacing(
                  1
                )} * 2)`,
              })}
            ></Box>
          )}
        </Box>
      )}
      {fieldGroupArray.map((_, index) => {
        return (
          <FieldGroupForm
            key={index}
            nbFieldGroup={fieldGroupArray.length}
            keyFieldPath={`${fieldName}[${index}]`}
            fieldsOptions={fieldsOptions}
            row={row}
            rows={rows}
            multipleSelection={multipleSelection}
            dynamicPath={dynamicPath}
            onRemoveGroup={() => {
              if (
                !fieldsOptions.limitMin ||
                (fieldsOptions.limitMin &&
                  fieldGroupArray.length > fieldsOptions.limitMin)
              ) {
                remove(index);
                if (fieldsOptions.autoFillPercent) {
                  redistributePercent(
                    form,
                    fieldName,
                    fieldGroupArray.length - 1,
                    fieldsOptions.autoFillPercent
                  );
                }
              }
            }}
            isNestedForm={isNested}
          />
        );
      })}
      {meta.touched && meta.error && typeof meta.error === "string" && (
        <Typography sx={{ color: "error.main", fontSize: "0.75rem" }}>
          {intl.formatMessage({ id: meta.error })}
        </Typography>
      )}
    </Box>
  );
};

const FieldGroupColumn = ({
  fieldsOptions,
  field,
  row,
  rows,
  multipleSelection,
  dynamicPath,
  meta,
}: {
  fieldsOptions: FieldGroupColumnFormDetailsFilled;
  field: FieldInputProps<any>;
  row?: GridRowModel;
  rows?: GridRowModel[];
  multipleSelection?: GridRowSelectionModel;
  dynamicPath?: boolean;
  meta: FieldMetaProps<any>;
}) => {
  const isNested = field.name.indexOf(".") > -1;
  const mergedFields = mergeArr(fieldsOptions);

  return (
    <FieldArray name={field.name}>
      {({ unshift, remove, form }) => (
        <FieldGroupContent
          fieldName={field.name}
          fieldsOptions={fieldsOptions}
          mergedFields={mergedFields}
          meta={meta}
          row={row}
          rows={rows}
          multipleSelection={multipleSelection}
          dynamicPath={dynamicPath}
          isNested={isNested}
          unshift={unshift}
          remove={remove}
          form={form}
        />
      )}
    </FieldArray>
  );
};

export default FieldGroupColumn;
